/**
 * @file VideoPreview.tsx
 *
 * @description A component for displaying and interacting with police video evidence.
 * This component renders police videos with optional transcript data overlaid on the video.
 * It supports both single video and paginated multi-video displays.
 *
 * @module Components
 * @requires React
 * @requires Material-UI
 */

import React, { useRef, useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  useTheme,
  Pagination,
  IconButton,
  Tooltip,
  Paper,
  Fade,
  alpha,
  Slider,
  CircularProgress,
  Collapse,
  Menu,
  MenuItem,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import ExpandIcon from '@mui/icons-material/Expand';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ChatIcon from '@mui/icons-material/Chat';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import DownloadIcon from "@mui/icons-material/Download";
import DownloadingIcon from "@mui/icons-material/Downloading";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import { VideoData, VideoStatus } from "../../shared/interfaces/VideoData";
import audioThumbnailLight from "../../assets/audio_thumbnail_light.svg";
import audioThumbnailDark from "../../assets/audio_thumbnail_dark.svg";
import { TranscriptData } from "./TranscriptViewer";
import { TranscriptOverlay } from "./TranscriptOverlay";
import { formatEvidenceDateTime } from "./formatEvidenceDateTime";
import { DownloadTranscriptLanguageModal } from "@/shared/ui/modals/DownloadTranscriptLanguageModal";
import { downloadTranscript } from "@/shared/api/transcriptDownloadApi";
import { usePermissions } from "@/shared/hooks/usePermissions";
import { canDownloadTranscript, canEditEvidenceItem } from "@/shared/auth/authorization";
import { useAuth } from "@workos-inc/authkit-react";
import { useToast } from "../../shared/toast";

/**
 * Props for the VideoPreview component
 * @interface VideoPreviewProps
 * @property {VideoData | VideoData[]} videoData - Single video or array of videos to display
 * @property {boolean} [isMinimized=false] - Whether the component is in minimized view mode
 * @property {() => void} [onToggleMinimize] - Callback function triggered when minimize/maximize button is clicked
 * @property {boolean} [isMinimizable=true] - Whether the component can be minimized/maximized
 */
interface VideoPreviewProps {
  videoData: VideoData | VideoData[];
  isMinimized?: boolean;
  onToggleMinimize?: () => void;
  isMinimizable?: boolean;
}

/**
 * @name VideoPreview
 *
 * @description VideoPreview component for displaying video evidence with OVERLAID transcript
 *
 * This V2 component displays video evidence with its associated information including
 * recording date, upload date, and evidence ID. Unlike V1, the transcript is
 * displayed as a semi-transparent overlay on top of the video content.
 *
 * @param {VideoPreviewProps} props - Component props
 *
 * @returns {React.ReactElement} The rendered VideoPreview component
 */
export const VideoPreview: React.FC<VideoPreviewProps> = ({
  videoData,
  isMinimized = false,
  onToggleMinimize,
  isMinimizable = true,
}) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0); // if multiple videos are to be shown, track the current page

  // Clamp page when the videoData array shrinks (e.g. evidence reload returns
  // fewer items). Without this, "Video X of Y" and the Pagination component
  // can render an out-of-range index.
  useEffect(() => {
    if (!Array.isArray(videoData)) return;
    const maxIndex = Math.max(0, videoData.length - 1);
    if (page > maxIndex) setPage(maxIndex);
  }, [videoData, page]);
  const theme = useTheme();
  
  // Player state
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0); // Current video playback time
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(false);

  const [isVideoLoading, setIsVideoLoading] = useState(true); // Video loading state
  const [isTransitioning, setIsTransitioning] = useState(false); // Page transition state
  const [showTranscriptOverlay, setShowTranscriptOverlay] = useState(true); // Toggle for overlay
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);
  // Download format chosen from the menu; non-null opens the language modal
  const [languageModalFormat, setLanguageModalFormat] = useState<"txt" | "docx" | null>(null);
  const permissions = usePermissions();
  const { user } = useAuth();
  const canDownload = canDownloadTranscript(permissions);
  const toast = useToast();
  
  const videoRef = useRef<HTMLVideoElement | null>(null); // Reference to the video element for programmatic control
  const containerRef = useRef<HTMLDivElement>(null);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Helper to format time
  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Video Handlers
  const handlePlayPause = useCallback(() => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  }, [isPlaying]);

  const handleVolumeChange = (_event: Event, newValue: number | number[]) => {
    const newVolume = Array.isArray(newValue) ? newValue[0] : newValue;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
      videoRef.current.muted = newVolume === 0;
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      const newMuted = !isMuted;
      setIsMuted(newMuted);
      videoRef.current.muted = newMuted;
      if (newMuted) {
        setVolume(0);
      } else {
        setVolume(1);
        videoRef.current.volume = 1;
      }
    }
  };

  const handleSeek = (_event: Event, newValue: number | number[]) => {
    const time = Array.isArray(newValue) ? newValue[0] : newValue;
    setCurrentTime(time);
    if (videoRef.current) {
      videoRef.current.currentTime = time;
    }
  };

  const handleFullscreen = useCallback(() => {
    if (!containerRef.current) return;

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().then(() => setIsFullscreen(true)).catch((err) => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false));
    }
  }, []);

  // Listen for fullscreen chagne events (in case user presses Escape)
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Controls Visibility
  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
  };

  const handleMouseLeave = () => {
    if (isPlaying) {
      setShowControls(false);
    }
  };

  // Pause when minimizing
  const handleToggleMinimize = () => {
    if (!isMinimized && isPlaying) {
      handlePlayPause();
    }
    if (onToggleMinimize) onToggleMinimize();
  }

  // Memoized time update handler
  const handleTimeUpdate = useCallback(
    (e: React.SyntheticEvent<HTMLVideoElement>) => {
      setCurrentTime(e.currentTarget.currentTime);
    },
    []
  );

  // Handle video load start/end
  const handleVideoLoadStart = useCallback(() => {
    setIsVideoLoading(true);
  }, []);

  const handleVideoCanPlay = useCallback(() => {
    setIsVideoLoading(false);
    if (videoRef.current) {
        setDuration(videoRef.current.duration);
    }
  }, []);

  const handleVideoEnded = () => {
    setIsPlaying(false);
    setShowControls(true);
  };

  /**
   * @name handlePageChange
   *
   * @description Handles page change in paginated video display with smooth transition
   *
   * @param {React.ChangeEvent<unknown>} _ - Unused event parameter
   * @param {number} value - New page number (1-indexed)
   */
  const handlePageChange = useCallback(
    (_: React.ChangeEvent<unknown>, value: number) => {
      setIsTransitioning(true);
      setTimeout(() => {
        setPage(value - 1);
        setIsVideoLoading(true);
        setIsTransitioning(false);
        // Reset transcript overlay state or keep user preference? Keep preference.
      }, 150);
    },
    []
  );

  // Reusable styles for metadata cards
  const metadataCardSx = {
    backgroundColor: theme.palette.mode === "dark" ? alpha(theme.palette.common.white, 0.03) : theme.palette.grey[50],
    p: 1.5,
    borderRadius: 2,
    height: "100%",
    border: `1px solid ${theme.palette.mode === "dark" ? theme.palette.divider : theme.palette.grey[200]}`,
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'center',
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      backgroundColor: theme.palette.mode === "dark" ? alpha(theme.palette.common.white, 0.05) : theme.palette.grey[100],
      borderColor: theme.palette.mode === "dark" ? alpha(theme.palette.common.white, 0.2) : theme.palette.grey[300],
    },
  };

  /**
   * @name handleLanguageDownload
   * @description Downloads the transcript in the selected language + the format
   * chosen from the menu. The file is generated server-side; the native-language
   * option uses the stored transcript, other languages are translated server-side.
   * On failure the language modal stays open so the user can retry.
   * @param {VideoData} video - The video data
   * @param {string} languageCode - The selected language code
   */
  const handleLanguageDownload = async (
    video: VideoData,
    languageCode: string,
  ) => {
    if (!languageModalFormat) return;
    try {
      await downloadTranscript({
        evidenceId: video.id,
        format: languageModalFormat,
        languageCode,
      });
      setLanguageModalFormat(null);
    } catch (error) {
      console.error("Failed to download transcript:", error);
      toast.error("Failed to download the transcript. Please try again.");
    }
  };

  /**
   * @name renderVideo
   *
   * @description Renders a single video card with metadata and transcript
   *
   * @param {VideoData} videoData - Video data to display
   *
   * @returns {React.ReactElement} Rendered video card
   */
  const renderVideo = (videoData: VideoData) => {
    const isAudio = videoData.fileType?.startsWith("audio") ?? false;
    const audioThumbnail = theme.palette.mode === "dark" ? audioThumbnailDark : audioThumbnailLight;
    // Per-video edit gate: editing your own upload needs evidence:edit_own,
    // editing someone else's needs evidence:edit_station. Uploader identity is
    // optional on VideoData; fail closed when it (or the viewer's id) is
    // missing — only a confirmed authenticated match counts as ownership, so
    // unknown uploaders fall back to requiring evidence:edit_station.
    const isUploader = Boolean(
      videoData.uploadedBy && user?.id && videoData.uploadedBy === user.id,
    );
    const canEdit = canEditEvidenceItem(permissions, isUploader);
    return (
    <Fade in={!isTransitioning} timeout={200}>
      <Box>
        {/* Minimized View (Toolbar) */}
        <Collapse in={isMinimized} unmountOnExit>
          <Paper
            elevation={0}
            onClick={handleToggleMinimize}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
              p: 1.5,
              borderRadius: 2,
              cursor: "pointer",
              background: theme.palette.mode === 'dark' 
                ? theme.palette.background.paper 
                : `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, ${theme.palette.grey[100]} 100%)`,                                                           
              border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.divider : theme.palette.grey[200]}`,
              "&:hover": {
                backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.05) : theme.palette.grey[100],
              }
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0, flexGrow: 1 }}>
              <PlayCircleOutlineIcon
                sx={{
                  color: theme.palette.primary.main,
                  fontSize: "1.5rem",
                  flexShrink: 0,
                }}
              />
              <Tooltip title={videoData.title} arrow placement="top">
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    color: theme.palette.text.primary,
                  }}
                >
                  {videoData.title}
                </Typography>
              </Tooltip>
            </Box>
            <Box display="flex" alignItems="center" gap={0.5}>
              {isMinimizable && (
                <>
                  <Typography variant="button" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                      View Video
                  </Typography>
                  <Tooltip title="View video" arrow>
                    <IconButton size="small">
                      <ExpandIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>
          </Paper>
        </Collapse>

        {/* Expanded View (YouTube-style Player) */}
        <Collapse in={!isMinimized} unmountOnExit timeout={500}>
            <Box>
              {!videoData.videoUrl ? (
              /* The backend omits videoUrl while the file isn't in S3 (still
                 fetching/compressing — or FAILED, which never arrives) — show
                 a message instead of a player that could never load. */
              <Paper
                elevation={2}
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  mb: 2,
                  position: "relative",
                  backgroundColor: "#000",
                  aspectRatio: "16/9",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1.5,
                  p: 3,
                }}
              >
                {isMinimizable && (
                  <Tooltip title="Minimize view">
                    <IconButton
                      onClick={handleToggleMinimize}
                      size="small"
                      sx={{ position: "absolute", top: 8, right: 8, color: "grey.400" }}
                    >
                      <UnfoldLessIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {videoData.status === VideoStatus.FAILED ? (
                  <>
                    <ErrorOutlineIcon sx={{ fontSize: 56, color: "grey.500" }} />
                    <Typography variant="h6" sx={{ color: "white", textAlign: "center" }}>
                      Video Failed to Download
                    </Typography>
                    <Typography variant="body2" sx={{ color: "grey.400", textAlign: "center" }}>
                      This video couldn't be retrieved. Try re-adding it from
                      the evidence page, or contact support if the problem
                      persists.
                    </Typography>
                  </>
                ) : (
                  <>
                    <DownloadingIcon sx={{ fontSize: 56, color: "grey.500" }} />
                    <Typography variant="h6" sx={{ color: "white", textAlign: "center" }}>
                      Video Not Yet Finished Downloading
                    </Typography>
                    <Typography variant="body2" sx={{ color: "grey.400", textAlign: "center" }}>
                      Please check again later.
                    </Typography>
                  </>
                )}
              </Paper>
              ) : (
              <Paper
                ref={containerRef}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                elevation={2}
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  mb: 2,
                  position: "relative",
                  backgroundColor: "#000",
                  aspectRatio: "16/9",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  ...(isAudio && {
                    backgroundImage: `url(${audioThumbnail})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }),
                }}
              >
                {/* 1. Video Element */}
                <video
                  ref={videoRef}
                  width="100%"
                  height="100%"
                  src={videoData.videoUrl}
                  poster={isAudio ? audioThumbnail : undefined}
                  onClick={handlePlayPause}
                  onTimeUpdate={handleTimeUpdate}
                  onLoadStart={handleVideoLoadStart}
                  onCanPlay={handleVideoCanPlay}
                  onEnded={handleVideoEnded}
                  playsInline
                  style={{ display: "block", objectFit: "contain" }}
                />

                {/* 2. Top Controls Overlay */}
                <Fade in={showControls || !isPlaying}>
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      p: 2,
                      background: "linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, transparent 100%)",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      zIndex: 2,
                      pointerEvents: "none", // let clicks pass through to video
                    }}
                  >
                    <Typography 
                        variant="h6" 
                        sx={{ color: "white", textShadow: "0 1px 2px rgba(0,0,0,0.8)", pointerEvents: "auto" }}
                    >
                        {videoData.title}
                    </Typography>
                    
                    <Box sx={{ pointerEvents: "auto", display: 'flex', gap: 1 }}>
                        {/* Top Right Controls if any */}
                    </Box>
                  </Box>
                </Fade>

                {/* 3. Center Info/Action Overlay */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    pointerEvents: "none",
                  }}
                >
                  {isVideoLoading ? (
                    <CircularProgress size={60} sx={{ color: "white" }} />
                  ) : (
                    <Fade in={!isPlaying}>
                        <Box
                           onClick={handlePlayPause} 
                           sx={{ 
                               pointerEvents: "auto", 
                               borderRadius: '50%', 
                               bgcolor: 'rgba(0, 0, 0, 0.5)',
                               p: 2,
                               cursor: 'pointer',
                               transition: 'transform 0.1s',
                               '&:active': { transform: 'scale(0.95)' }
                           }}
                        >
                            <PlayArrowIcon sx={{ fontSize: 60, color: "white" }} />
                        </Box>
                    </Fade>
                  )}
                </Box>

                {/* 4. Transcript Overlay */}
                {!!videoData.transcript && showTranscriptOverlay && (
                    <TranscriptOverlay 
                        transcript={videoData.transcript as TranscriptData}
                        currentTimeStamp={currentTime}
                        handleParagraphClick={(timestamp) => {
                            if (videoRef.current) {
                                videoRef.current.currentTime = timestamp;
                            }
                        }}
                        sx={{ bottom: showControls ? 90 : 24, transition: 'bottom 0.3s ease' }}
                    />
                )}

                {/* 5. Bottom Controls Bar */}
                <Fade in={showControls || !isPlaying}>
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: "linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 100%)",
                      px: 2,
                      pb: 1.5,
                      pt: 4,
                      zIndex: 2,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                    }}
                  >
                    {/* Timeline Slider */}
                    <Slider
                      value={currentTime}
                      min={0}
                      max={duration || 100}
                      onChange={handleSeek}
                      size="small"
                      sx={{
                        color: theme.palette.error.main, // YouTube red
                        height: 4,
                        p: 0,
                        "& .MuiSlider-thumb": {
                          width: 12,
                          height: 12,
                          transition: "0.2s",
                          "&:before": { boxShadow: "0 2px 12px 0 rgba(0,0,0,0.4)" },
                          "&:hover, &.Mui-focusVisible": { boxShadow: `0px 0px 0px 8px ${alpha(theme.palette.error.main, 0.16)}` },
                        },
                        "& .MuiSlider-rail": { opacity: 0.3, backgroundColor: "#fff" },
                      }}
                    />
                    
                    {/* Controls Row */}
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <IconButton onClick={handlePlayPause} sx={{ color: "white" }} size="small">
                                {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                            </IconButton>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', '&:hover .volume-slider': { width: 100, opacity: 1, ml: 1 } }}>
                                <IconButton onClick={toggleMute} sx={{ color: "white" }} size="small">
                                    {isMuted || volume === 0 ? <VolumeOffIcon /> : <VolumeUpIcon />}
                                </IconButton>
                                <Box className="volume-slider" sx={{ width: 0, opacity: 0, overflow: 'hidden', transition: 'all 0.2s', ml: 0 }}>
                                    <Slider 
                                        value={isMuted ? 0 : volume} 
                                        min={0} max={1} step={0.1} 
                                        onChange={handleVolumeChange}
                                        sx={{ color: "white", height: 2, width: '90%' }}
                                    />
                                </Box>
                            </Box>

                            {/* Transcript Toggle */}
                            {!!videoData.transcript && (
                                <Tooltip title={showTranscriptOverlay ? "Hide Transcript" : "Show Transcript"}>
                                    <IconButton 
                                        onClick={() => setShowTranscriptOverlay(!showTranscriptOverlay)}
                                        sx={{ color: "white" }}
                                        size="small"
                                    >
                                        {showTranscriptOverlay ? <ChatIcon fontSize="small" /> : <ChatBubbleOutlineIcon fontSize="small" />}
                                    </IconButton>
                                </Tooltip>
                            )}
                            
                            <Typography variant="body2" sx={{ color: "white", ml: 1, fontFamily: 'monospace' }}>
                                {formatTime(currentTime)} / {formatTime(duration)}
                            </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton onClick={handleFullscreen} sx={{ color: "white" }} size="small">
                                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                            </IconButton>
                            {/* Minimize Button */}
                            {isMinimizable && (
                                <Tooltip title="Minimize view">
                                    <IconButton onClick={handleToggleMinimize} sx={{ color: "white" }} size="small">
                                        <UnfoldLessIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                  </Box>
                </Fade>
              </Paper>
              )}

              {/* Video metadata - Compact Row */}
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                {/* Recording date */}
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Box sx={metadataCardSx}>
                      <Typography
                        variant="caption"
                        color="primary"
                        fontWeight={700}
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, letterSpacing: '0.5px' }}
                      >
                        <CalendarTodayIcon sx={{ fontSize: "0.8rem" }} /> RECORDED
                      </Typography>
                    <Typography variant="body2" fontWeight={500} color="text.primary">
                      {formatEvidenceDateTime(videoData.recordedOn)}
                    </Typography>
                  </Box>
                </Grid>

                {/* Upload date */}
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Box sx={metadataCardSx}>
                      <Typography
                        variant="caption"
                        color="primary"
                        fontWeight={700}
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, letterSpacing: '0.5px' }}
                      >
                        <CloudUploadIcon sx={{ fontSize: "0.8rem" }} /> UPLOADED
                      </Typography>
                    <Typography variant="body2" fontWeight={500} color="text.primary">
                      {formatEvidenceDateTime(videoData.uploadedOn)}
                    </Typography>
                  </Box>
                </Grid>

                {/* Evidence ID */}
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Box sx={metadataCardSx}>
                      <Typography
                        variant="caption"
                        color="primary"
                        fontWeight={700}
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, letterSpacing: '0.5px' }}
                      >
                        <FingerprintIcon sx={{ fontSize: "0.8rem" }} /> ID
                      </Typography>
                    <Typography variant="body2" fontWeight={500} color="text.primary" sx={{ fontFamily: 'monospace' }}>
                      {videoData.id}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              {/* Download / edit transcript buttons */}
              {!!videoData.transcript && (
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1.5 }}>
                  {canEdit && (
                  <Tooltip title="Open the transcript editor" arrow>
                    <Box
                      component="button"
                      onClick={() => navigate(`/transcripts/${videoData.id}`)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 2,
                        py: 0.75,
                        border: `1px solid ${theme.palette.mode === "dark" ? theme.palette.divider : theme.palette.grey[300]}`,
                        borderRadius: 2,
                        backgroundColor: theme.palette.mode === "dark"
                          ? alpha(theme.palette.common.white, 0.05)
                          : theme.palette.grey[50],
                        color: theme.palette.text.secondary,
                        cursor: 'pointer',
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          backgroundColor: theme.palette.primary.main,
                          borderColor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                        },
                      }}
                    >
                      <RecordVoiceOverIcon sx={{ fontSize: "0.9rem" }} />
                      <Typography variant="caption" fontWeight={600} sx={{ letterSpacing: '0.3px', color: 'inherit' }}>
                        Edit Transcript
                      </Typography>
                    </Box>
                  </Tooltip>
                  )}
                  {canDownload && (
                  <Tooltip title="Choose download format" arrow>
                    <Box
                      component="button"
                      onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 2,
                        py: 0.75,
                        border: `1px solid ${theme.palette.mode === "dark" ? theme.palette.divider : theme.palette.grey[300]}`,
                        borderRadius: 2,
                        backgroundColor: theme.palette.mode === "dark"
                          ? alpha(theme.palette.common.white, 0.05)
                          : theme.palette.grey[50],
                        color: theme.palette.text.secondary,
                        cursor: 'pointer',
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          backgroundColor: theme.palette.primary.main,
                          borderColor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                        },
                      }}
                    >
                      <DownloadIcon sx={{ fontSize: "0.9rem" }} />
                      <Typography variant="caption" fontWeight={600} sx={{ letterSpacing: '0.3px', color: 'inherit' }}>
                        Download Transcript
                      </Typography>
                    </Box>
                  </Tooltip>
                  )}
                  {canDownload && (
                    <>
                      <Menu
                        anchorEl={downloadMenuAnchor}
                        open={Boolean(downloadMenuAnchor)}
                        onClose={() => setDownloadMenuAnchor(null)}
                        onClick={() => setDownloadMenuAnchor(null)}
                      >
                        <MenuItem onClick={() => setLanguageModalFormat("txt")}>
                          Download as TXT
                        </MenuItem>
                        <MenuItem onClick={() => setLanguageModalFormat("docx")}>
                          Download as DOCX
                        </MenuItem>
                      </Menu>
                      <DownloadTranscriptLanguageModal
                        open={languageModalFormat !== null}
                        onClose={() => setLanguageModalFormat(null)}
                        onDownload={(languageCode) =>
                          handleLanguageDownload(videoData, languageCode)
                        }
                      />
                    </>
                  )}
                </Box>
              )}
            </Box>
        </Collapse>
      </Box>
    </Fade>
    );
  };

  // Render multiple videos with pagination or a single video.
  // Empty arrays / null happen when every evidence record 404s — render nothing
  // rather than crash on videoData[page].fileType.
  if (Array.isArray(videoData)) {
    if (videoData.length === 0) return null;
    // Clamp at render time as well so the same render that triggers the
    // useEffect above still shows a valid index instead of a stale one.
    const clampedPage = Math.max(0, Math.min(page, videoData.length - 1));
    const current = videoData[clampedPage];
    return (
      <Box>
        {renderVideo(current)}
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mt: 2,
            p: 1,
            borderRadius: 2,
            backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.03) : theme.palette.grey[50],
            border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.divider : theme.palette.grey[200]}`,
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mr: 2 }}
          >
            Video {clampedPage + 1} of {videoData.length}
          </Typography>
          <Pagination
            count={videoData.length}
            page={clampedPage + 1}
            onChange={handlePageChange}
            color="primary"
            size="small"
            sx={{
              "& .MuiPaginationItem-root": {
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "scale(1.1)",
                },
              },
            }}
          />
        </Paper>
      </Box>
    );
  }

  if (!videoData) return null;
  return renderVideo(videoData);
};
